import { useState, useEffect, useContext } from 'react';
import { Box, Grid, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { OpenInNewOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCrud } from '../../../lib/hooks';
import { getErrorMessage } from '../../../lib/utils';
import { validateEventForSave } from './validateEventForSave';
import generalConfig, { getPlayBaseUrl } from '../../../config/general';
import {
    AppContext,
    SttButton,
    SttButtonCancel,
    SttButtonDelete,
    SttButtonRecover,
    SttButtonSave,
    SttFormCheck,
    SttFormDate,
    SttFormSelect,
    SttFormText,
    SttFormTextarea,
    SttFormTime,
    SttSportSelector,
} from '../../../sporttia/all';
import { PERMISSIONS } from '../../../lib/permissions';

type Sport = {
    id: string | number;
    name?: string;
};

type Poster = {
    id?: string | number;
};

type Category = {
    id?: string | number;
};

type Event = {
    id?: string | number;
    name: string;
    description?: string;
    kind?: string;
    difficulty?: string;
    status: string;
    maxParticipants?: number | string;
    onlyCertified?: boolean;
    showParticipants?: boolean;
    signupOnline?: boolean;
    maxPlayers?: number | string;
    type: string;
    sport: Sport;
    poster?: Poster;
    inscIni: string;
    inscEnd: string;
    ini: string;
    end: string;
    paymentForms?: string[];
    categories?: Category[];
    trash?: boolean | number;
};

type Props = {
    item: Event;
    onSave?: (event: Event) => void;
    onDelete?: (event: Event) => void;
};

type Constant = {
    caption: string;
    value: string;
};

export default function EventDetails({
    item,
    onSave,
    onDelete,
}: Props): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [event, setEvent, setProperty, , Post, Put, Delete] =
        useCrud<Event>(item);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (item) {
            setEvent(item);
        }
    }, [item]);

    function saveEvent(): void {
        if (!validateEventForSave(event)) {
            cxt.showMessage(
                'E',
                t('event.fillRequiredFields') ||
                    'Rellena los campos obligatorios: Nombre y Deporte',
            );
            return;
        }

        setLoading(true);
        const params = {
            name: event.name,
            description: event.description,
            kind: event.kind,
            difficulty: event.difficulty,
            status: event.status,
            maxParticipants: event.maxParticipants,
            onlyCertified: event.onlyCertified,
            showParticipants: event.showParticipants,
            signupOnline: event.signupOnline,
            maxPlayers: event.maxPlayers,
            type: event.type,
            idSport: event.sport.id,
            idPoster: event?.poster?.id || null,
            inscIni: event.inscIni,
            inscEnd: event.inscEnd,
            ini: event.ini,
            end: event.end,
            paymentForms: event.paymentForms,
        };

        if (event.id) {
            Put(`/events/${event.id}`, params)
                .then((response: unknown) => {
                    const res = response as { event?: Event };
                    if (res.event) {
                        if (onSave && onSave.constructor === Function) {
                            onSave({
                                ...res.event,
                                categories: event?.categories,
                            });
                        }
                    }
                })
                .catch((error: unknown) => {
                    cxt.showMessage('E', getErrorMessage(error));
                })
                .finally(() => setLoading(false));
        } else {
            Post(`/events`, params)
                .then((response: unknown) => {
                    const res = response as { event?: Event };
                    if (res.event) {
                        if (onSave && onSave.constructor === Function) {
                            onSave(res.event);
                        }
                    }
                })
                .catch((error: unknown) => {
                    cxt.showMessage('E', getErrorMessage(error));
                })
                .finally(() => setLoading(false));
        }
    }

    function deleteEvent(): void {
        Delete(`/events/${event.id}`).then((response: unknown) => {
            const res = response as { event?: Event };
            if (res.event) {
                if (onDelete && onDelete.constructor === Function) {
                    onDelete(res.event);
                }
            }
        });
    }

    function recoverEvent(): void {
        Put(`/events/${event.id}/recover`).then((response: unknown) => {
            const res = response as { event?: Event };
            if (res.event) {
                if (onSave && onSave.constructor === Function) {
                    onSave(res.event);
                }
            }
        });
    }

    function setDate({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void {
        if (!name) return;
        const currentEventDate = event[name as keyof Event] as string;
        const currentDate = parseISO(currentEventDate);
        const newDate = parseISO(value as string);
        const finalDate = `${format(newDate, 'yyyy-MM-dd')} ${format(
            currentDate,
            'HH:mm:ss',
        )}`;

        const parsedFinalDate = parseISO(finalDate);
        setProperty({
            name: name as keyof Event,
            value: (isValid(parsedFinalDate)
                ? finalDate
                : format(
                      new Date(),
                      'yyyy-MM-dd 23:30:00',
                  )) as Event[keyof Event],
        });
    }

    function setTime({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void {
        if (!name) return;
        const currentEventDate = event[name as keyof Event] as string;
        const currentDate = parseISO(currentEventDate);
        setProperty({
            name: name as keyof Event,
            value: format(
                currentDate,
                `yyyy-MM-dd ${value}:00`,
            ) as Event[keyof Event],
        });
    }

    function togglePaymentForm(type: string): void {
        const found =
            event.paymentForms?.find((pf: string) => pf === type) !== undefined;

        setProperty({
            name: 'paymentForms',
            value: found
                ? event.paymentForms?.filter((pf: string) => pf !== type)
                : [...(event.paymentForms || []), type],
        });
    }

    if (!event) {
        return null;
    }

    const checkRowStyle = { paddingTop: 0, paddingBottom: 0 };

    return (
        <>
            <Box my={3}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, lg: 4 }}>
                        <SttFormText
                            caption={t('Name')}
                            name="name"
                            defVal={event.name}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Event,
                                    value: value as Event[keyof Event],
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                        <SttFormSelect
                            caption={t('Inscription')}
                            name="type"
                            defVal={event.type}
                            options={[
                                {
                                    caption: t('ET.NORMAL'),
                                    value: 'NORMAL',
                                },
                                {
                                    caption: t('ET.TOURNAMENT'),
                                    value: 'TOURNAMENT',
                                },
                            ]}
                            disabled={!!event.id}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Event,
                                    value: value as Event[keyof Event],
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <SttFormTextarea
                            caption={t('Description')}
                            name="description"
                            defVal={event.description ?? undefined}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Event,
                                    value: value as Event[keyof Event],
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                        <SttSportSelector
                            fullWidth
                            defVal={event.sport?.name ?? undefined}
                            onChange={(sport: { id: number; name?: string }) =>
                                setProperty({
                                    name: 'sport',
                                    value: {
                                        id: sport.id,
                                        name: sport.name,
                                    } as Event[keyof Event],
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                        <SttFormSelect
                            caption={t('Difficulty')}
                            name="difficulty"
                            defVal={event.difficulty}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Event,
                                    value: value as Event[keyof Event],
                                })
                            }
                            cleanable
                            options={
                                cxt?.constants
                                    ?.eventDifficulties as unknown as Constant[]
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                        <SttFormSelect
                            caption={t('Status')}
                            name="status"
                            defVal={event.status}
                            options={[
                                {
                                    caption: t('Active'),
                                    value: 'ACTIVE',
                                },
                                {
                                    caption: t('Inactive'),
                                    value: 'NOT_ACTIVE',
                                },
                            ]}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Event,
                                    value: value as Event[keyof Event],
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                        {event.type === 'TOURNAMENT' ? (
                            <SttFormText
                                caption={t('MaxPlayersTeam')}
                                name="maxPlayers"
                                defVal={
                                    event.maxPlayers?.toString() ?? undefined
                                }
                                onChange={({ name, value }) =>
                                    setProperty({
                                        name: name as keyof Event,
                                        value: value as Event[keyof Event],
                                    })
                                }
                            />
                        ) : (
                            <SttFormText
                                caption={t('sc.event.main.maxUsers')}
                                name="maxParticipants"
                                defVal={
                                    event.maxParticipants?.toString() ??
                                    undefined
                                }
                                onChange={({ name, value }) =>
                                    setProperty({
                                        name: name as keyof Event,
                                        value: value as Event[keyof Event],
                                    })
                                }
                            />
                        )}
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <SttFormDate
                            caption={`${t('Inscription')} ${t('From').toLowerCase()}`}
                            name="inscIni"
                            defVal={event.inscIni}
                            onChange={setDate}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <SttFormTime
                            defVal={
                                event.inscIni
                                    ? format(parseISO(event.inscIni), 'HH:mm')
                                    : ''
                            }
                            name="inscIni"
                            onChange={setTime}
                            interval={15}
                            min="00:00:00"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <SttFormDate
                            caption={t('Until')}
                            name="inscEnd"
                            defVal={event.inscEnd}
                            onChange={setDate}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <SttFormTime
                            defVal={
                                event.inscEnd
                                    ? format(parseISO(event.inscEnd), 'HH:mm')
                                    : ''
                            }
                            name="inscEnd"
                            onChange={setTime}
                            interval={15}
                            min="00:00:00"
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <SttFormDate
                            caption={t('sc.event.main.dateIniEvent')}
                            name="ini"
                            defVal={event.ini}
                            onChange={setDate}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <SttFormTime
                            name="ini"
                            defVal={
                                event.ini
                                    ? format(parseISO(event.ini), 'HH:mm')
                                    : ''
                            }
                            onChange={setTime}
                            interval={15}
                            min="00:00:00"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <SttFormDate
                            caption={t('Until')}
                            name="end"
                            defVal={event.end}
                            onChange={setDate}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <SttFormTime
                            name="end"
                            defVal={
                                event.end
                                    ? format(parseISO(event.end), 'HH:mm')
                                    : ''
                            }
                            onChange={setTime}
                            interval={15}
                            min="00:00:00"
                        />
                    </Grid>

                    <Grid style={checkRowStyle} size={{ xs: 12 }}>
                        <SttFormCheck
                            caption={t('sc.event.main.certifiedUsers')}
                            name="onlyCertified"
                            checked={event.onlyCertified}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Event,
                                    value: value as Event[keyof Event],
                                })
                            }
                        />
                    </Grid>
                    <Grid style={checkRowStyle} size={{ xs: 12 }}>
                        <SttFormCheck
                            caption={t('sc.event.main.showLastUsers')}
                            name="showParticipants"
                            checked={event.showParticipants}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Event,
                                    value: value as Event[keyof Event],
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Divider />
                    </Grid>
                    <Grid style={checkRowStyle} size={{ xs: 12 }}>
                        <SttFormCheck
                            caption={t('page.sc.events.signupOnline')}
                            name="signupOnline"
                            checked={event.signupOnline}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Event,
                                    value: value as Event[keyof Event],
                                })
                            }
                        />
                    </Grid>
                    <Grid style={checkRowStyle} size={{ xs: 12 }}>
                        <SttFormCheck
                            caption={t('page.sc.events.later')}
                            checked={
                                event.paymentForms?.find(
                                    (pf: string) => pf === '',
                                ) !== undefined
                            }
                            onChange={() => togglePaymentForm('')}
                            disabled={!event.signupOnline}
                        />
                    </Grid>
                    <Grid style={checkRowStyle} size={{ xs: 12 }}>
                        <SttFormCheck
                            caption={t('page.sc.events.purse')}
                            checked={
                                event.paymentForms?.find(
                                    (pf: string) => pf === 'PURSE',
                                ) !== undefined
                            }
                            onChange={() => togglePaymentForm('PURSE')}
                            disabled={!event.signupOnline}
                        />
                    </Grid>
                    <Grid style={checkRowStyle} size={{ xs: 12 }}>
                        <SttFormCheck
                            caption={t('page.sc.events.tpv')}
                            checked={
                                event.paymentForms?.find(
                                    (pf: string) => pf === 'TPV',
                                ) !== undefined
                            }
                            onChange={() => togglePaymentForm('TPV')}
                            disabled={!event.signupOnline}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Box className="mt-6 flex justify-start gap-4 flex-wrap">
                {event.id && !event.trash && (
                    <SttButtonDelete
                        onClick={deleteEvent}
                        permission={PERMISSIONS.EVENT.DELETE}
                    />
                )}

                {!event.trash && (
                    <SttButtonSave
                        loading={loading}
                        onClick={saveEvent}
                        permission={
                            event.id
                                ? PERMISSIONS.EVENT.MODIFY
                                : PERMISSIONS.EVENT.CREATE
                        }
                    />
                )}

                {!event.id && (
                    <SttButtonCancel
                        onClick={() => {
                            navigate(-1);
                        }}
                    />
                )}

                {event.id && !event.trash && (
                    <SttButton
                        caption={t('GoToEvent')}
                        icon={<OpenInNewOutlined fontSize="small" />}
                        onClick={() => {
                            window.open(
                                `${getPlayBaseUrl(generalConfig.environment)}/center/${cxt?.sc?.id}/event/${event.id}`,
                                '_blank',
                            );
                        }}
                    />
                )}

                {event.trash && (
                    <SttButtonRecover
                        onClick={recoverEvent}
                        permission={PERMISSIONS.EVENT.DELETE}
                    />
                )}
            </Box>
        </>
    );
}
